import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Plus, Trash2, X, DollarSign, FileText, Zap, CheckCircle2, Calendar as CalendarIcon, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayMonth = () => new Date().toISOString().slice(0, 7);
const thisYear = () => String(new Date().getFullYear());

const emptyOwner = { owner_name: '', email: '', phone: '', address: '', tin: '', revenue_share_percent: 70, payment_method: 'ACH', notes: '', vehicle_ids: [] };
const emptyPayout = { owner_id: '', period_month: todayMonth(), gross_revenue: 0, expenses: 0, revenue_share_percent: 70, notes: '' };

const OwnerPayoutsTab = () => {
  const [owners, setOwners] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutTotals, setPayoutTotals] = useState({});
  const [vehiclesPL, setVehiclesPL] = useState([]);
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [ownerForm, setOwnerForm] = useState(emptyOwner);
  const [payoutForm, setPayoutForm] = useState(emptyPayout);
  const [filterOwner, setFilterOwner] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [statement, setStatement] = useState(null);
  const [statementYear, setStatementYear] = useState(thisYear());
  const [generatePeriod, setGeneratePeriod] = useState(todayMonth());
  const [busy, setBusy] = useState(false);

  const loadOwners = useCallback(async () => {
    const r = await fetch(`${API}/api/command-center/owners`);
    const d = await r.json();
    setOwners(d.owners || []);
  }, []);

  const loadPayouts = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterOwner !== 'all') params.set('owner_id', filterOwner);
    if (filterStatus !== 'all') params.set('status', filterStatus);
    const r = await fetch(`${API}/api/command-center/payouts?${params}`);
    const d = await r.json();
    setPayouts(d.payouts || []);
    setPayoutTotals(d.totals || {});
  }, [filterOwner, filterStatus]);

  const loadVehiclesPL = useCallback(async () => {
    const r = await fetch(`${API}/api/command-center/vehicle-pl`);
    const d = await r.json();
    setVehiclesPL(d.vehicles || []);
  }, []);

  useEffect(() => { loadOwners(); loadVehiclesPL(); }, [loadOwners, loadVehiclesPL]);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const submitOwner = async () => {
    if (!ownerForm.owner_name.trim()) { toast.error('Owner name required'); return; }
    setBusy(true);
    try {
      if (editingOwner) {
        const r = await fetch(`${API}/api/command-center/owners/${editingOwner}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ownerForm),
        });
        if (!r.ok) throw new Error((await r.json()).detail || 'Update failed');
        toast.success('Owner updated');
      } else {
        const r = await fetch(`${API}/api/command-center/owners`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ownerForm),
        });
        if (!r.ok) throw new Error((await r.json()).detail || 'Create failed');
        toast.success('Owner added');
      }
      setShowOwnerForm(false); setEditingOwner(null); setOwnerForm(emptyOwner);
      await loadOwners();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const editOwner = (o) => {
    setEditingOwner(o.owner_id);
    setOwnerForm({ ...emptyOwner, ...o, tin: '' });  // TIN must be re-entered (we only show masked)
    setShowOwnerForm(true);
  };

  const deleteOwner = async (id) => {
    if (!window.confirm('Delete this owner? Past payouts will be archived but kept.')) return;
    await fetch(`${API}/api/command-center/owners/${id}`, { method: 'DELETE' });
    toast.success('Owner deleted');
    loadOwners(); loadPayouts();
  };

  const submitPayout = async () => {
    if (!payoutForm.owner_id) { toast.error('Pick an owner'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/payouts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payoutForm),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Create failed');
      toast.success('Payout created');
      setShowPayoutForm(false); setPayoutForm(emptyPayout);
      loadPayouts();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const markPaid = async (p) => {
    const ref = window.prompt('Payment reference (ACH/check #)?', '');
    if (ref === null) return;
    await fetch(`${API}/api/command-center/payouts/${p.payout_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'paid', payment_reference: ref }),
    });
    toast.success(`Marked ${f(p.owner_payout)} paid to ${p.owner_name}`);
    loadPayouts();
  };

  const deletePayout = async (id) => {
    if (!window.confirm('Delete this payout record?')) return;
    await fetch(`${API}/api/command-center/payouts/${id}`, { method: 'DELETE' });
    toast.success('Payout deleted');
    loadPayouts();
  };

  const generateForMonth = async () => {
    if (!/^\d{4}-\d{2}$/.test(generatePeriod)) { toast.error('Period must be YYYY-MM'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/payouts/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period_month: generatePeriod }),
      });
      const d = await r.json();
      toast.success(`Generated ${d.generated_count} · skipped ${d.skipped_count}`);
      loadPayouts();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const viewStatement = async (owner) => {
    const r = await fetch(`${API}/api/command-center/owners/${owner.owner_id}/statement?year=${statementYear}`);
    const d = await r.json();
    setStatement(d);
  };

  const printStatement = () => window.print();

  return (
    <SwissCard className="p-4 sm:p-6 bg-white" data-testid="owner-payouts-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-red-600" />
          <h2 className="text-xl font-bold tracking-tight">Owner Payouts <span className="text-xs font-normal text-gray-500 ml-2">Revenue share · 1099-ready</span></h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => { setEditingOwner(null); setOwnerForm(emptyOwner); setShowOwnerForm(true); }} className="bg-black hover:bg-gray-800 text-white" data-testid="add-owner-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Owner
          </Button>
          <Button size="sm" onClick={() => { setPayoutForm(emptyPayout); setShowPayoutForm(true); }} variant="outline" data-testid="add-payout-btn">
            <DollarSign className="h-4 w-4 mr-1" /> Manual Payout
          </Button>
        </div>
      </div>

      {/* Owners summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Tile label="Owners" value={owners.length} />
        <Tile label="Payouts Pending" value={payoutTotals.pending_count || 0} accent="amber" />
        <Tile label="Pending $" value={f(payouts.filter(p => p.status !== 'paid').reduce((s, p) => s + (p.owner_payout || 0), 0))} accent="red" />
        <Tile label="Paid YTD" value={f(payouts.filter(p => p.status === 'paid' && (p.period_month || '').startsWith(thisYear())).reduce((s, p) => s + (p.owner_payout || 0), 0))} accent="green" />
      </div>

      {/* Generate row */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-5 flex items-center gap-3 flex-wrap" data-testid="generate-row">
        <Zap className="h-4 w-4 text-amber-500" />
        <span className="text-sm font-semibold">Auto-generate from Vehicle P&L</span>
        <Input type="month" value={generatePeriod} onChange={e => setGeneratePeriod(e.target.value)} className="h-8 w-40" data-testid="generate-period" />
        <Button size="sm" onClick={generateForMonth} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="generate-payouts-btn">Generate</Button>
        <span className="text-xs text-gray-500 ml-auto">Pulls revenue_log entries for each owner's vehicles</span>
      </div>

      {/* Owners list */}
      <div className="mb-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Fleet Owners</h3>
        {owners.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6 text-center border border-dashed rounded">No owners yet — add one to start tracking revenue shares.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Owner</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">TIN</th>
                  <th className="text-right px-3 py-2">Share</th>
                  <th className="text-left px-3 py-2">Pay Method</th>
                  <th className="text-left px-3 py-2">Vehicles</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {owners.map(o => (
                  <tr key={o.owner_id} className="border-t hover:bg-gray-50" data-testid={`owner-row-${o.owner_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{o.owner_name}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{o.owner_id}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs">{o.email || '—'}</div>
                      <div className="text-[10px] text-gray-500">{o.phone || ''}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{o.tin_masked || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{o.revenue_share_percent}%</td>
                    <td className="px-3 py-2 text-xs">{o.payment_method || 'ACH'}</td>
                    <td className="px-3 py-2 text-xs">{(o.vehicle_ids || []).length}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => viewStatement(o)} className="text-xs text-blue-600 hover:underline mr-3" data-testid={`view-statement-${o.owner_id}`}>1099</button>
                      <button onClick={() => editOwner(o)} className="text-xs text-gray-700 hover:underline mr-3">Edit</button>
                      <button onClick={() => deleteOwner(o.owner_id)} className="text-xs text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payouts list */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Payouts</h3>
          <div className="flex gap-2">
            <Select value={filterOwner} onValueChange={setFilterOwner}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-owner"><SelectValue placeholder="All owners" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {owners.map(o => <SelectItem key={o.owner_id} value={o.owner_id}>{o.owner_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {payouts.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6 text-center border border-dashed rounded">No payouts in the current filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Period</th>
                  <th className="text-left px-3 py-2">Owner</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">Expenses</th>
                  <th className="text-right px-3 py-2">Net</th>
                  <th className="text-right px-3 py-2">Share %</th>
                  <th className="text-right px-3 py-2">Payout</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.payout_id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{p.period_month}</td>
                    <td className="px-3 py-2">{p.owner_name}</td>
                    <td className="px-3 py-2 text-right">{f(p.gross_revenue)}</td>
                    <td className="px-3 py-2 text-right text-red-700">{f(p.expenses)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{f(p.net_revenue)}</td>
                    <td className="px-3 py-2 text-right">{p.revenue_share_percent}%</td>
                    <td className="px-3 py-2 text-right font-bold text-green-700">{f(p.owner_payout)}</td>
                    <td className="px-3 py-2 text-center">
                      {p.status === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3" /> PAID</span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">PENDING</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {p.status !== 'paid' && (
                        <button onClick={() => markPaid(p)} className="text-xs text-green-700 hover:underline mr-3" data-testid={`mark-paid-${p.payout_id}`}>Mark Paid</button>
                      )}
                      <button onClick={() => deletePayout(p.payout_id)} className="text-xs text-red-600 hover:underline" title="Delete"><Trash2 className="h-3.5 w-3.5 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Owner modal */}
      {showOwnerForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowOwnerForm(false)}>
          <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="owner-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingOwner ? 'Edit Owner' : 'Add Fleet Owner'}</h3>
              <button onClick={() => setShowOwnerForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Legal name / LLC *"><Input value={ownerForm.owner_name} onChange={e => setOwnerForm({ ...ownerForm, owner_name: e.target.value })} data-testid="owner-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><Input value={ownerForm.email} onChange={e => setOwnerForm({ ...ownerForm, email: e.target.value })} /></Field>
                <Field label="Phone"><Input value={ownerForm.phone} onChange={e => setOwnerForm({ ...ownerForm, phone: e.target.value })} /></Field>
              </div>
              <Field label="Mailing address (for 1099)"><Input value={ownerForm.address} onChange={e => setOwnerForm({ ...ownerForm, address: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={editingOwner ? "TIN / EIN (re-enter to change)" : "TIN / EIN"}><Input value={ownerForm.tin} onChange={e => setOwnerForm({ ...ownerForm, tin: e.target.value })} placeholder="XX-XXXXXXX" data-testid="owner-tin" /></Field>
                <Field label="Revenue share %"><Input type="number" value={ownerForm.revenue_share_percent} onChange={e => setOwnerForm({ ...ownerForm, revenue_share_percent: e.target.value })} data-testid="owner-share" /></Field>
              </div>
              <Field label="Payment method">
                <Select value={ownerForm.payment_method} onValueChange={v => setOwnerForm({ ...ownerForm, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACH">ACH</SelectItem>
                    <SelectItem value="Check">Check</SelectItem>
                    <SelectItem value="Wire">Wire</SelectItem>
                    <SelectItem value="Zelle">Zelle</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Linked vehicles (multi-select)">
                <div className="border rounded-md p-2 max-h-40 overflow-y-auto bg-gray-50">
                  {vehiclesPL.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No Vehicle P&L records yet — add them in the Vehicle P&L tab first.</p>
                  ) : vehiclesPL.map(v => (
                    <label key={v.vehicle_pl_id} className="flex items-center gap-2 py-1 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(ownerForm.vehicle_ids || []).includes(v.vehicle_pl_id)}
                        onChange={(e) => {
                          const ids = ownerForm.vehicle_ids || [];
                          setOwnerForm({ ...ownerForm, vehicle_ids: e.target.checked ? [...ids, v.vehicle_pl_id] : ids.filter(x => x !== v.vehicle_pl_id) });
                        }}
                      />
                      <span>{v.vehicle_name} <span className="text-gray-400">· {v.category}</span></span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Notes"><Input value={ownerForm.notes} onChange={e => setOwnerForm({ ...ownerForm, notes: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowOwnerForm(false)}>Cancel</Button>
              <Button onClick={submitOwner} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="owner-submit">{editingOwner ? 'Save' : 'Add Owner'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payout modal */}
      {showPayoutForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPayoutForm(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="payout-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Manual Payout</h3>
              <button onClick={() => setShowPayoutForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Owner *">
                <Select value={payoutForm.owner_id} onValueChange={v => {
                  const o = owners.find(x => x.owner_id === v);
                  setPayoutForm({ ...payoutForm, owner_id: v, revenue_share_percent: o ? o.revenue_share_percent : 70 });
                }}>
                  <SelectTrigger data-testid="payout-owner"><SelectValue placeholder="Pick owner…" /></SelectTrigger>
                  <SelectContent>
                    {owners.map(o => <SelectItem key={o.owner_id} value={o.owner_id}>{o.owner_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Period (YYYY-MM)"><Input type="month" value={payoutForm.period_month} onChange={e => setPayoutForm({ ...payoutForm, period_month: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Gross revenue $"><Input type="number" value={payoutForm.gross_revenue} onChange={e => setPayoutForm({ ...payoutForm, gross_revenue: parseFloat(e.target.value) || 0 })} data-testid="payout-gross" /></Field>
                <Field label="Expenses $"><Input type="number" value={payoutForm.expenses} onChange={e => setPayoutForm({ ...payoutForm, expenses: parseFloat(e.target.value) || 0 })} data-testid="payout-expenses" /></Field>
              </div>
              <Field label="Revenue share %"><Input type="number" value={payoutForm.revenue_share_percent} onChange={e => setPayoutForm({ ...payoutForm, revenue_share_percent: parseFloat(e.target.value) || 0 })} /></Field>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <div className="flex justify-between"><span>Net revenue</span><span className="font-semibold">{f(payoutForm.gross_revenue - payoutForm.expenses)}</span></div>
                <div className="flex justify-between text-green-700 font-bold mt-1"><span>Owner payout</span><span>{f((payoutForm.gross_revenue - payoutForm.expenses) * (payoutForm.revenue_share_percent / 100))}</span></div>
              </div>
              <Field label="Notes"><Input value={payoutForm.notes} onChange={e => setPayoutForm({ ...payoutForm, notes: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowPayoutForm(false)}>Cancel</Button>
              <Button onClick={submitPayout} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="payout-submit">Create Payout</Button>
            </div>
          </div>
        </div>
      )}

      {/* 1099 Statement viewer */}
      {statement && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:bg-white print:p-0" onClick={() => setStatement(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 print:max-w-none print:rounded-none print:shadow-none" onClick={e => e.stopPropagation()} data-testid="statement-modal">
            <div className="flex items-center justify-between mb-5 print:hidden">
              <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-red-600" /> 1099-Ready Statement</h3>
              <div className="flex gap-2">
                <Input type="number" value={statementYear} onChange={e => setStatementYear(e.target.value)} className="w-24 h-8" />
                <Button size="sm" onClick={() => viewStatement({ owner_id: statement.owner.owner_id })}>Refresh</Button>
                <Button size="sm" variant="outline" onClick={printStatement}><Printer className="h-4 w-4" /></Button>
                <button onClick={() => setStatement(null)}><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="border-b border-gray-200 pb-4 mb-4">
              <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Annual Owner Statement</p>
              <h2 className="text-2xl font-black tracking-tight mt-1">{statement.owner.owner_name}</h2>
              <p className="text-xs text-gray-500 mt-1">Tax year {statement.year} · TIN {statement.owner.tin_masked || '—'} · Share {statement.owner.revenue_share_percent}%</p>
              <p className="text-xs text-gray-500">{statement.owner.address}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <Tile label="Gross" value={f(statement.totals.gross_revenue)} small />
              <Tile label="Expenses" value={f(statement.totals.expenses)} small accent="red" />
              <Tile label="Paid out" value={f(statement.totals.owner_payout_paid)} small accent="green" />
              <Tile label="Pending" value={f(statement.totals.owner_payout_pending)} small accent="amber" />
            </div>

            {statement.totals.ready_for_1099 && (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-900 mb-4">
                <strong>1099-NEC required.</strong> Total paid to this owner ({f(statement.totals.owner_payout_paid)}) exceeds the IRS $600 reporting threshold.
              </div>
            )}

            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-2 py-1.5">Period</th><th className="text-right px-2 py-1.5">Gross</th><th className="text-right px-2 py-1.5">Exp</th><th className="text-right px-2 py-1.5">Net</th><th className="text-right px-2 py-1.5">Payout</th><th className="text-center px-2 py-1.5">Status</th></tr>
              </thead>
              <tbody>
                {statement.payouts.map(p => (
                  <tr key={p.payout_id} className="border-t">
                    <td className="px-2 py-1.5 font-mono">{p.period_month}</td>
                    <td className="px-2 py-1.5 text-right">{f(p.gross_revenue)}</td>
                    <td className="px-2 py-1.5 text-right text-red-700">{f(p.expenses)}</td>
                    <td className="px-2 py-1.5 text-right">{f(p.net_revenue)}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{f(p.owner_payout)}</td>
                    <td className="px-2 py-1.5 text-center text-[10px]">{(p.status || 'pending').toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-black font-bold">
                  <td className="px-2 py-2">Total {statement.totals.months_logged} months</td>
                  <td className="px-2 py-2 text-right">{f(statement.totals.gross_revenue)}</td>
                  <td className="px-2 py-2 text-right text-red-700">{f(statement.totals.expenses)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right text-green-700">{f(statement.totals.owner_payout_total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <p className="text-[10px] text-gray-400 mt-4 text-center print:mt-8">
              ACE Auto Concierge · Atlanta, GA · (678) 846-3009 · This statement is for the owner's records and does not constitute a filed 1099-NEC.
            </p>
          </div>
        </div>
      )}
    </SwissCard>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Tile = ({ label, value, accent, small }) => {
  const colorMap = {
    red: 'text-red-700 border-red-200 bg-red-50',
    amber: 'text-amber-800 border-amber-200 bg-amber-50',
    green: 'text-green-700 border-green-200 bg-green-50',
  };
  const cls = colorMap[accent] || 'text-gray-800 border-gray-200 bg-gray-50';
  return (
    <div className={`border rounded-lg px-3 py-2 ${cls}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className={`mt-0.5 font-black ${small ? 'text-base' : 'text-xl'}`}>{value}</p>
    </div>
  );
};

export default OwnerPayoutsTab;
